import { type Config } from 'tailwindcss'

export const extendedTheme = {
	colors: {
		border: 'hsla(var(--border), <alpha-value>)',
		input: {
			DEFAULT: 'hsla(var(--input), <alpha-value>)',
			invalid: 'hsla(var(--input-invalid), <alpha-value>)',
		},
		ring: {
			DEFAULT: 'hsla(var(--ring), <alpha-value>)',
			invalid: 'hsla(var(--foreground-destructive), <alpha-value>)',
		},
		background: {
			DEFAULT: 'hsla(var(--background), <alpha-value>)',
			100: 'hsla(var(--background-100), <alpha-value>)',
			200: 'hsla(var(--background-200), <alpha-value>)',
			300: 'hsla(var(--background-300), <alpha-value>)',
			400: 'hsla(var(--background-400), <alpha-value>)',
			600: 'hsla(var(--background-600), <alpha-value>)',
			700: 'hsla(var(--background-700), <alpha-value>)',
			800: 'hsla(var(--background-800), <alpha-value>)',
		},
		foreground: {
			DEFAULT: 'hsla(var(--foreground), <alpha-value>)',
			inverse: 'hsla(var(--foreground-inverse), <alpha-value>)',
			destructive: 'hsla(var(--foreground-destructive), <alpha-value>)',
		},
		primary: {
			DEFAULT: 'hsla(var(--primary), <alpha-value>)',
			100: 'hsla(var(--primary-100), <alpha-value>)',
			200: 'hsla(var(--primary-200), <alpha-value>)',
			300: 'hsla(var(--primary-300), <alpha-value>)',
			400: 'hsla(var(--primary-400), <alpha-value>)',
			600: 'hsla(var(--primary-600), <alpha-value>)',
			700: 'hsla(var(--primary-700), <alpha-value>)',
			800: 'hsla(var(--primary-800), <alpha-value>)',
			foreground: 'hsla(var(--primary-foreground), <alpha-value>)',
		},
		secondary: {
			DEFAULT: 'hsla(var(--secondary), <alpha-value>)',
			100: 'hsla(var(--secondary-100), <alpha-value>)',
			200: 'hsla(var(--secondary-200), <alpha-value>)',
			300: 'hsla(var(--secondary-300), <alpha-value>)',
			400: 'hsla(var(--secondary-400), <alpha-value>)',
			600: 'hsla(var(--secondary-600), <alpha-value>)',
			700: 'hsla(var(--secondary-700), <alpha-value>)',
			800: 'hsla(var(--secondary-800), <alpha-value>)',
			foreground: 'hsla(var(--secondary-foreground), <alpha-value>)',
		},
		tertiary: {
			DEFAULT: 'hsla(var(--tertiary), <alpha-value>)',
			100: 'hsla(var(--tertiary-100), <alpha-value>)',
			200: 'hsla(var(--tertiary-200), <alpha-value>)',
			300: 'hsla(var(--tertiary-300), <alpha-value>)',
			400: 'hsla(var(--tertiary-400), <alpha-value>)',
			600: 'hsla(var(--tertiary-600), <alpha-value>)',
			700: 'hsla(var(--tertiary-700), <alpha-value>)',
			800: 'hsla(var(--tertiary-800), <alpha-value>)',
			foreground: 'hsla(var(--tertiary-foreground), <alpha-value>)',
		},
		destructive: {
			DEFAULT: 'hsla(var(--destructive), <alpha-value>)',
			foreground: 'hsla(var(--destructive-foreground), <alpha-value>)',
		},
		toast: {
			DEFAULT: 'hsla(var(--toast), <alpha-value>)',
			foreground: 'hsla(var(--toast-foreground), <alpha-value>)',
		},
		muted: {
			DEFAULT: 'hsla(var(--muted), <alpha-value>)',
			foreground: 'hsla(var(--muted-foreground), <alpha-value>)',
		},
		popover: {
			DEFAULT: 'hsla(var(--popover), <alpha-value>)',
			foreground: 'hsla(var(--popover-foreground), <alpha-value>)',
		},
		card: {
			DEFAULT: 'hsla(var(--card), <alpha-value>)',
			foreground: 'hsla(var(--card-foreground), <alpha-value>)',
		},
	},
	borderColor: {
		DEFAULT: 'hsla(var(--border), <alpha-value>)',
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 2px)',
		sm: 'calc(var(--radius) - 4px)',
	},
	fontSize: {
		// 1rem = 16px
		/** 72px size / 76px high / bold */
		mega: ['4.5rem', { lineHeight: '4.75rem', fontWeight: '900' }],
		/** 56px size / 62px high / bold */
		h1: ['3.5rem', { lineHeight: '3.875rem', fontWeight: '700' }],
		/** 36px size / 40px high / bold */
		h2: ['2.25rem', { lineHeight: '2.5rem', fontWeight: '700' }],
		/** 32px size / 36px high / bold */
		h3: ['2rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 28px size / 36px high / bold */
		h4: ['1.75rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 24px size / 32px high / bold */
		h5: ['1.5rem', { lineHeight: '2rem', fontWeight: '700' }],
		/** 16px size / 20px high / bold */
		h6: ['1rem', { lineHeight: '1.25rem', fontWeight: '700' }],

		/** 32px size / 36px high / normal */
		'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
		/** 28px size / 36px high / normal */
		'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
		/** 24px size / 32px high / normal */
		'body-lg': ['1.5rem', { lineHeight: '2rem' }],
		/** 20px size / 28px high / normal */
		'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
		/** 16px size / 20px high / normal */
		'body-sm': ['1rem', { lineHeight: '1.25rem' }],
		/** 14px size / 18px high / normal */
		'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
		/** 12px size / 16px high / normal */
		'body-2xs': ['0.75rem', { lineHeight: '1rem' }],

		/** 18px size / 24px high / semibold */
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		/** 12px size / 16px high / bold */
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},
	fontFamily: {
		sans: 'var(--font-sans)'
	}
} satisfies Config['theme']
